package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.dsl.ir.expr.ExprBlobReader
import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.dsl.ir.expr.StmtNode
import top.wcpe.allincore.dsl.ir.model.UiIr
import top.wcpe.allincore.dsl.ir.model.UiScreenFont
import top.wcpe.allincore.dsl.ir.model.UiSubscribeBlock
import top.wcpe.allincore.dsl.ir.model.action.ReservedActions
import top.wcpe.allincore.dsl.ir.model.action.UiAction
import top.wcpe.allincore.dsl.ir.model.hud.UiHudMeta
import top.wcpe.allincore.dsl.ir.model.hud.UiKeyBinding
import top.wcpe.allincore.ui.runtime.element.FontStyle
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.event.KeyEvent
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.host.AnimationHost
import top.wcpe.allincore.ui.runtime.host.TimerHost
import top.wcpe.allincore.ui.runtime.host.ToastManager
import top.wcpe.allincore.ui.runtime.host.UiTask
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.api.DateApi
import top.wcpe.allincore.ui.runtime.ir.api.DebugApi
import top.wcpe.allincore.ui.runtime.ir.api.DisplayApi
import top.wcpe.allincore.ui.runtime.ir.api.KeyBindingHost
import top.wcpe.allincore.ui.runtime.ir.api.LocalApi
import top.wcpe.allincore.ui.runtime.ir.api.PersistApiHolder
import top.wcpe.allincore.ui.runtime.ir.api.SessionApi
import top.wcpe.allincore.ui.runtime.ir.api.SlotApi
import top.wcpe.allincore.ui.runtime.ir.api.TimeApi
import top.wcpe.allincore.ui.runtime.ir.api.TimerApi
import top.wcpe.allincore.ui.runtime.ir.api.UiCommandApi
import top.wcpe.allincore.ui.runtime.ir.api.VanillaApi
import top.wcpe.allincore.ui.runtime.ir.call.ActionDispatcher
import top.wcpe.allincore.ui.runtime.ir.call.CallDispatcher
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.ir.request.RequestDispatcher
import top.wcpe.allincore.ui.runtime.ir.request.RequestRegistry
import top.wcpe.allincore.ui.runtime.ir.state.SharedStateAccess
import top.wcpe.allincore.ui.runtime.ir.state.SharedStateAccessProvider
import top.wcpe.allincore.ui.runtime.ir.state.SharedStateRegistry
import top.wcpe.allincore.ui.runtime.ir.subscribe.SubscribeRouter
import top.wcpe.allincore.ui.runtime.ir.subscribe.Subscription
import top.wcpe.allincore.ui.runtime.reactive.Computed
import top.wcpe.allincore.ui.runtime.reactive.Reactive
import top.wcpe.allincore.ui.runtime.reactive.ReactiveWrapper
import top.wcpe.allincore.ui.runtime.reactive.Signal
import top.wcpe.allincore.ui.runtime.theme.ThemeEnvironment
import top.wcpe.allincore.ui.runtime.theme.ThemeLookup

/**
 * 会话装配 / 入口协作者：把 [UiIr] 装配成 [IrSession]——构建 state 信号表、解析 actions、
 * 注入各类 API、构建根元素、注册 screen 级事件 / HUD keybinding / 静态订阅。
 *
 * 它构建根元素时调 [ElementTreeBuilder.buildElement]、补挂 overlay 调 [ElementOverlayAttacher]，
 * screen 级事件转 Map 调 [EventMapBuilder]。门面 [IrInterpreter] 的 public `@JvmStatic`
 * 入口方法纯转发到本协作者，保持对外 ABI 不变。
 */
@Suppress("TooManyFunctions")
internal object IrSessionAssembler {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.IrInterpreter")

    /** 入口（兼容旧调用）：仅绑定 ActionDispatcher，不支持 request。 */
    fun interpret(ir: UiIr, dispatcher: ActionDispatcher): UIElement<*> =
        interpretSession(ir, dispatcher, RequestDispatcher.NOOP, RequestRegistry()).root

    /** 入口：支持 request 的完整解释。 */
    fun interpret(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
    ): UIElement<*> = interpretSession(ir, actionDispatcher, requestDispatcher, registry).root

    /** 解释并返回完整 session（含 states/actions/evalContext，供外部驱动使用）。 */
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
    ): IrSession = interpretSession(ir, actionDispatcher, requestDispatcher, registry, LocalApi.NOOP)

    /** 解释并返回完整 session（增加 LocalApi 注入入口）。 */
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
    ): IrSession = interpretSession(ir, actionDispatcher, requestDispatcher, registry, localApi, DslLogger.STDOUT)

    /** C6：解释并返回完整 session，可注入 DslLogger（print/log 输出端）。 */
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
    ): IrSession = interpretSession(
        ir,
        actionDispatcher,
        requestDispatcher,
        registry,
        localApi,
        logger,
        SlotCountResolver.EMPTY,
        null,
    )

    /** B4：解释并返回完整 session，注入 SlotCountResolver 以支持 dynamic inventory / slotGrid 运行时展开。 */
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
    ): IrSession =
        interpretSession(ir, actionDispatcher, requestDispatcher, registry, localApi, logger, slotCountResolver, null)

    /** 完整入口（含 DslExecutor）：平台层创建 session 时使用，DSL 逻辑在独立线程执行。 */
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
    ): IrSession = interpretSession(
        ir, actionDispatcher, requestDispatcher, registry, localApi,
        logger, slotCountResolver, executor, SlotApi.NOOP,
    )

    /** 完整入口（含 DslExecutor 与 SlotApi）：客户端接入响应式槽位读取时使用。 */
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
        slotApi: SlotApi?,
    ): IrSession = interpretSession(
        ir, actionDispatcher, requestDispatcher, registry, localApi,
        logger, slotCountResolver, executor, slotApi, SubscribeRouter.NOOP,
    )

    /** B7：完整入口（含 SubscribeRouter）：支持订阅路由的 session 创建。 */
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
        slotApi: SlotApi?,
        subscribeRouter: SubscribeRouter?,
    ): IrSession = interpretSession(
        ir, actionDispatcher, requestDispatcher, registry, localApi, logger,
        slotCountResolver, executor, slotApi, subscribeRouter,
        KeyBindingHost.NOOP,
    )

    /** Phase 4：完整入口（含 KeyBindingHost）：支持 HUD keybinding 的 session 创建。 */
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
        slotApi: SlotApi?,
        subscribeRouter: SubscribeRouter?,
        keyBindingHost: KeyBindingHost,
    ): IrSession = interpretSession(
        ir, actionDispatcher, requestDispatcher, registry, localApi, logger,
        slotCountResolver, executor, slotApi, subscribeRouter, keyBindingHost, null,
    )

    /**
     * 完整入口（含 DisplayApi）：客户端接入展示物品读取（display.read）时使用。
     *
     * displayApi 必须与 slotApi 同样在构建元素树前注入——`item = display.read(...)` 的 Effect
     * 首跑发生在 buildElement 期间，晚注入会让 Effect 绑死 NOOP 的空视图信号、推送永不生效。
     */
    @Suppress("LongMethod", "LongParameterList", "CyclomaticComplexMethod")
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
        slotApi: SlotApi?,
        subscribeRouter: SubscribeRouter?,
        keyBindingHost: KeyBindingHost,
        displayApi: DisplayApi? = null,
        params: Map<String, Any?>? = null,
    ): IrSession {
        val sharedStateAccess = resolveSharedStateAccess(requestDispatcher)
        val states: Map<String, Signal<Any?>> = buildStates(ir)
        // 把 session / persist 默认值预注册到当前注入域，候选解释不会污染全局或旧 session。
        seedSharedStates(ir, sharedStateAccess)
        val actionDefs: Map<String, UiAction> = ir.actions
        val actions: MutableMap<String, List<StmtNode>> = parseActions(actionDefs)
        val themeLookup = ThemeLookup.of(ir.themeTokens)
        val themeManager = top.wcpe.allincore.ui.runtime.theme.ThemeManager(ir.availableThemes)
        val themeEnvironment = ThemeEnvironment(themeLookup, themeManager, ir.themeDirectory)
        // rendererRegistry 由客户端创建 RenderRoot 后通过 IrSession.bindRendererRegistry(...) 绑定
        val baseEvalCtx = EvalContext.ofThemeEnvironment(
            states, ir.stateDefaults.types, registry, requestDispatcher, themeEnvironment,
            if (localApi == null) LocalApi.NOOP else localApi, ir.functions,
            if (logger == null) DslLogger.STDOUT else logger, executor, themeManager, null,
            sharedStateAccess,
        ).withSlotApi(if (slotApi == null) SlotApi.NOOP else slotApi)
            .withDisplayApi(if (displayApi == null) DisplayApi.NOOP else displayApi)
        val evalCtx = if (params.isNullOrEmpty()) baseEvalCtx else baseEvalCtx.withParams(params)
        // M6-h Round 2：每个 session 持有一个 ToastManager，注入到 EvalContext 供内建 toast() 使用
        val toastManager = ToastManager()
        evalCtx.attachToastManager(toastManager)
        // 任务 4：每个 session 持有一个 TimerHost，绑定到 DslExecutor 串行调度；session dispose 时统一 cancel
        if (executor != null) {
            evalCtx.attachTimerHost(TimerHost(executor))
        }
        // B7：注入订阅路由器
        val router = if (subscribeRouter == null) SubscribeRouter.NOOP else subscribeRouter
        evalCtx.attachSubscribeRouter(router)
        // Phase 1：统一注入层——无状态 API 一次性注入
        evalCtx.attachVanillaApi(VanillaApi.NOOP)
        evalCtx.attachTimeApi(TimeApi.NOOP)
        evalCtx.attachDateApi(DateApi.NOOP)
        evalCtx.attachTimerApi(TimerApi.NOOP)
        evalCtx.attachDebugApi(DebugApi.NOOP)
        evalCtx.attachSessionApi(SessionApi(sharedStateAccess::clearSession))
        evalCtx.attachPersistApi(PersistApiHolder.get())
        // derived 派生值：每条表达式包成懒求值 Computed，注入 evalCtx 供 derived.x 读取。
        // 必须在构建元素树（buildElement）前注入，因为元素属性表达式可能读 derived.x。
        evalCtx.attachDerived(buildDerived(ir, evalCtx))
        registry.attachEvalContext(evalCtx)
        val ctx = IrInterpreter.Context.of(
            ir.screenId, states, actions, actionDefs, ir.animations,
            ir.stateMachines, actionDispatcher, evalCtx,
            if (slotCountResolver == null) SlotCountResolver.EMPTY else slotCountResolver,
        )
        ctx.ir = ir
        val root: UIElement<*> = ElementTreeBuilder.buildElement(ir.root, ctx.withRootNode(ir.root))
        ElementOverlayAttacher.attachElementOverlays(root, ir)
        // M6-i：构造完元素树后注入动画宿主，让内建 animate(name, target?) 能拿到 root + animations
        evalCtx.attachAnimationHost(AnimationHost(root, ir.animations))
        // Phase 4：注入 UiCommandApi，让 ui("id").toggleExpand() 等元素命令能正确分派
        evalCtx.attachUiApi(
            UiCommandApi(
                evalCtx.animationHost,
                evalCtx.renderThreadExecutor,
            ),
        )
        val screenOnKey: UiEventHandler<KeyEvent>? = buildScreenOnKey(actions, evalCtx)
        val screenOnKeyRelease: UiEventHandler<KeyEvent>? = buildScreenOnKeyRelease(actions, evalCtx)
        val screenOnMouseDown: UiEventHandler<MouseEvent>? = buildScreenOnMouseDown(actions, evalCtx)
        val screenOnMouseUp: UiEventHandler<MouseEvent>? = buildScreenOnMouseUp(actions, evalCtx)
        val screenOnMouseMove: UiEventHandler<MouseEvent>? = buildScreenOnMouseMove(actions, evalCtx)
        // Stage 2：把 SCREEN_OPEN / SCREEN_CLOSE 两个保留键从 actionMap 中移除，
        // 防止 EventBindingDispatcher 把它们当作普通 action 误派发；交由 IrSession
        // 的 runOpenIfPresent / runCloseIfPresent 在屏幕生命周期点显式调度。
        val openBody: List<StmtNode>? = actions.remove(ReservedActions.SCREEN_OPEN)
        val closeBody: List<StmtNode>? = actions.remove(ReservedActions.SCREEN_CLOSE)
        // show / hide 块体：与 open / close 同样从 actionMap 移除，避免被误派发为普通 action。
        val showBody: List<StmtNode>? = actions.remove(ReservedActions.SCREEN_SHOW)
        val hideBody: List<StmtNode>? = actions.remove(ReservedActions.SCREEN_HIDE)
        // Stage 7：HUD tick body 来自 IR hud meta；screen 类 IR 的 tickBodyBytes=null。
        val tickBytes: ByteArray? = ir.hudMeta.tickBodyBytes
        val tickBody: List<StmtNode>? = if (tickBytes == null) null else ExprBlobReader.readActionBody(tickBytes)
        // B7：遍历 IR 中的静态订阅声明，反序列化 handler 并注册到 router
        val autoSubs: List<Subscription> = buildAutoSubscriptions(ir.subscribes, evalCtx, router, executor)
        val session = IrSession(
            root, states, ir.stateDefaults.types, actions, evalCtx, registry, requestDispatcher,
            screenOnKey, screenOnKeyRelease, screenOnMouseDown, screenOnMouseUp, screenOnMouseMove,
            toastManager, openBody, closeBody, showBody, hideBody, autoSubs, tickBody, keyBindingHost,
        )
        // 把 screenId 注入 session，便于 open/close 块异常日志定位（"screen open 块执行异常 [screenId=park/.../...]"）
        session.screenId = ir.screenId
        // 界面级默认字体：把 IR 的 UiScreenFont 转成 FontStyle 存到 session，
        // 由 bindRendererRegistry 写入 RendererRegistry.pageFont，经 FontAccess 页面层作用于本界面所有文字。
        session.pageFont = toPageFont(ir.screenFont)
        // Phase 4：注册 HUD keybinding 到槽位池
        registerKeyBindings(ir, session)
        return session
    }

    /** OverlayLayer 使用的最小子树实例化入口，共享调用方 EvalContext。 */
    fun instantiateOverlayRoot(ir: UiIr?, evalCtx: EvalContext?): UIElement<*>? {
        if (ir == null || evalCtx == null) {
            return null
        }
        val actions: MutableMap<String, List<StmtNode>> = parseActions(ir.actions)
        val ctx = IrInterpreter.Context.of(
            ir.screenId, evalCtx.states, actions, ir.actions, ir.animations,
            ir.stateMachines, ActionDispatcher.NOOP, evalCtx, SlotCountResolver.EMPTY,
        )
        ctx.ir = ir
        val root: UIElement<*> = ElementTreeBuilder.buildElement(ir.root, ctx.withRootNode(ir.root))
        ElementOverlayAttacher.attachElementOverlays(root, ir)
        return root
    }

    /** 把 IR 的界面字体载体转成 runtime FontStyle；null / 空声明返回 null（不设页面字体）。 */
    private fun toPageFont(sf: UiScreenFont?): FontStyle? {
        if (sf == null || sf.isEmpty) {
            return null
        }
        return FontStyle.builder()
            .name(sf.name)
            .shadow(sf.shadow)
            .bold(sf.bold)
            .italic(sf.italic)
            .build()
    }

    /**
     * B7：把 IR 中的静态订阅声明反序列化为 handler 并注册到 router。
     *
     * paramName 非空 → lambda 形态（readActionBody 得到 stmt 列表）；
     * paramName 为空 → funRef 形态（readExpr 得到 FunRef 节点）。
     *
     * 容错策略：单条静态订阅"反序列化或注册失败"以 ERROR 日志记录后跳过，
     * 不阻断整个 session 创建。原因：DSL 编译期的语义检查器（SubscribeChecker /
     * ReferenceResolver）已在编译阶段把 handler 形态/参数个数等典型错误拦下，
     * 运行期到达此处通常是 IR 损坏或 router 实现 bug——让屏幕仍能渲染并通过日志暴露
     * 问题，比让整个屏幕加载失败更友好。运行期 handler 调用阶段的异常另由
     * [buildSubscribeHandler] 内部以 WARN 隔离，不与本注册阶段混淆。
     */
    @Suppress("TooGenericExceptionCaught")
    private fun buildAutoSubscriptions(
        blocks: List<UiSubscribeBlock>?,
        evalCtx: EvalContext,
        router: SubscribeRouter,
        executor: DslExecutor?,
    ): List<Subscription> {
        if (blocks == null || blocks.isEmpty()) {
            return emptyList()
        }
        val subs: MutableList<Subscription> = ArrayList(blocks.size)
        for (block in blocks) {
            try {
                val handler: UiEventHandler<Map<String, Any?>> =
                    buildSubscribeHandler(block, evalCtx, executor)
                val sub = router.subscribe(block.namespace, block.topic, handler)
                subs.add(sub)
            } catch (t: Throwable) {
                // 注册阶段失败：bodyBytes 损坏 / FunRef 求值失败 / router 实现异常
                LOGGER.error("[ui] 静态订阅注册失败（已跳过该条目，session 仍正常创建）namespace=${block.namespace} topic=${block.topic}", t)
            }
        }
        return subs
    }

    /**
     * B7：根据 UiSubscribeBlock 构造 handler UiEventHandler。
     *
     * 异常分层：
     * - 注册阶段（bodyBytes 反序列化 / FunRef 求值失败）异常向上抛，由
     *   [buildAutoSubscriptions] 以 ERROR 记录并跳过该条订阅，不影响其它条目；
     * - 运行期 handler 调用异常用 WARN 隔离，下条 dispatch 仍可正常触发。
     *
     * 调度策略：lambda 形态走"反序列化 stmt 列表 + withLocals + declareVar"内联执行；
     * funRef 形态先把 IR 中的 FunRef 节点求值为 FunRefValue，再统一交给
     * [CallDispatcher.wrapSubscribeCallable] 处理 executor 串行化与异常隔离，
     * 与运行期 `net.subscribe(...)` 路径共用同一调度模板。
     */
    @Suppress("TooGenericExceptionCaught")
    private fun buildSubscribeHandler(
        block: UiSubscribeBlock,
        evalCtx: EvalContext,
        executor: DslExecutor?,
    ): UiEventHandler<Map<String, Any?>> {
        val paramName = block.paramName
        val namespace = block.namespace
        val topic = block.topic
        if (!paramName.isEmpty()) {
            // lambda 形态：反序列化 body stmt 列表，按需 withLocals 注入 paramName
            val body: List<StmtNode> = ExprBlobReader.readActionBody(block.bodyBytes)
            return UiEventHandler { payload ->
                val task = UiTask {
                    try {
                        val derived = evalCtx.withLocals(emptyMap<String, Any?>())
                        derived.declareVar(paramName, payload)
                        ExprEvaluator.execute(body, derived)
                    } catch (t: Throwable) {
                        LOGGER.warn("[ui] 订阅 handler（lambda）运行期执行异常 namespace=$namespace topic=$topic", t)
                    }
                }
                if (executor != null) {
                    executor.submit(task)
                } else {
                    task.run()
                }
            }
        }
        // funRef 形态：反序列化为 ExprNode，求值得到可调用对象，统一走 wrapSubscribeCallable。
        val exprNode: ExprNode = ExprBlobReader.readExpr(block.bodyBytes)
        val callable: Any? = ExprEvaluator.eval(exprNode, evalCtx)
        return CallDispatcher.wrapSubscribeCallable(callable, evalCtx, namespace, topic, "subscribe-handler")
    }

    /**
     * 从 actionMap 取出 screen 级 onKey lambda 体（保留键名见 [ReservedActions.SCREEN_ON_KEY]），
     * 包装为 `UiEventHandler<KeyEvent>`：每次调用时把 KeyEvent 转为 Map 注入 EvalContext 的
     * var 帧栈中（lambda 形参在 codegen 阶段被编译为 LocalRef，对应运行期的 var 帧而非 locals map），
     * 再执行 stmt 列表。
     *
     * 未声明 onKey 时返回 null，UIRuntimeScreen 据此跳过 screen 级派发。
     */
    @Suppress("TooGenericExceptionCaught")
    private fun buildScreenOnKey(
        actions: MutableMap<String, List<StmtNode>>,
        evalCtx: EvalContext,
    ): UiEventHandler<KeyEvent>? {
        // 与 SCREEN_OPEN / SCREEN_CLOSE 同样语义：reserved 键从 actionMap 移除，
        // 避免 EventBindingDispatcher 在事件绑定阶段把它们当作普通 action 误派发。
        val body = actions.remove(ReservedActions.SCREEN_ON_KEY)
        if (body == null || body.isEmpty()) return null
        return UiEventHandler { event ->
            val eventMap = EventMapBuilder.keyEventToMap(event)
            // withLocals 派生独立 var 帧栈，避免污染外层；同时把 event 注入到栈顶帧供 LocalRef 解析。
            val local = evalCtx.withLocals(emptyMap<String, Any?>())
            local.declareVar(ReservedActions.SCREEN_ON_KEY_PARAM, eventMap)
            try {
                ExprEvaluator.execute(body, local)
            } catch (t: Throwable) {
                LOGGER.warn("[ui] screen onKey 执行异常 key=${event.key()}", t)
            }
        }
    }

    /**
     * 与 [buildScreenOnKey] 严格对称,但只接 GLFW RELEASE 派发。
     *
     * 从 actionMap 取出 screen 级 onKeyRelease lambda 体（保留键名见
     * [ReservedActions.SCREEN_ON_KEY_RELEASE]），包装为
     * `UiEventHandler<KeyEvent>`。未声明 onKeyRelease 时返回 null,UIRuntimeScreen
     * 据此跳过 screen 级松开派发,避免历史 onKey 把 RELEASE 也误触发的循环 bug。
     */
    @Suppress("TooGenericExceptionCaught")
    private fun buildScreenOnKeyRelease(
        actions: MutableMap<String, List<StmtNode>>,
        evalCtx: EvalContext,
    ): UiEventHandler<KeyEvent>? {
        val body = actions.remove(ReservedActions.SCREEN_ON_KEY_RELEASE)
        if (body == null || body.isEmpty()) return null
        return UiEventHandler { event ->
            val eventMap = EventMapBuilder.keyEventToMap(event)
            val local = evalCtx.withLocals(emptyMap<String, Any?>())
            local.declareVar(ReservedActions.SCREEN_ON_KEY_RELEASE_PARAM, eventMap)
            try {
                ExprEvaluator.execute(body, local)
            } catch (t: Throwable) {
                LOGGER.warn("[ui] screen onKeyRelease 执行异常 key=${event.key()}", t)
            }
        }
    }

    /**
     * RFC-INPUT-EVENT-UNIFICATION Stage 4：与 [buildScreenOnKey] 完全对称。
     *
     * 从 actionMap 取出 screen 级 onMouseDown lambda 体（保留键名见
     * [ReservedActions.SCREEN_ON_MOUSE_DOWN]），包装为 `UiEventHandler<MouseEvent>`：
     * 每次调用时把 MouseEvent 转为 Map 注入 EvalContext 栈顶帧（lambda 形参在 codegen
     * 阶段被编译为 LocalRef，对应运行期的 var 帧），再执行 stmt 列表。
     *
     * 未声明 onMouseDown 时返回 null，UIRuntimeScreen 据此跳过 screen 级派发。
     */
    @Suppress("TooGenericExceptionCaught")
    private fun buildScreenOnMouseDown(
        actions: MutableMap<String, List<StmtNode>>,
        evalCtx: EvalContext,
    ): UiEventHandler<MouseEvent>? {
        // reserved 键从 actionMap 移除，避免 EventBindingDispatcher 误派发为普通 action。
        val body = actions.remove(ReservedActions.SCREEN_ON_MOUSE_DOWN)
        if (body == null || body.isEmpty()) return null
        return UiEventHandler { event ->
            val eventMap = EventMapBuilder.mouseEventToMap(event)
            val local = evalCtx.withLocals(emptyMap<String, Any?>())
            local.declareVar(ReservedActions.SCREEN_ON_MOUSE_DOWN_PARAM, eventMap)
            try {
                ExprEvaluator.execute(body, local)
            } catch (t: Throwable) {
                LOGGER.warn("[ui] screen onMouseDown 执行异常 button=${event.buttonName()}", t)
            }
        }
    }

    /**
     * RFC-MOUSE-EXTENDED-EVENTS Stage A：与 [buildScreenOnMouseDown] 完全对称。
     *
     * 从 actionMap 取出 screen 级 onMouseUp lambda 体（保留键名见
     * [ReservedActions.SCREEN_ON_MOUSE_UP]），包装为 `UiEventHandler<MouseEvent>`：
     * 每次调用时把 MouseEvent 转为 Map 注入 EvalContext 栈顶帧，再执行 stmt 列表。
     *
     * 未声明 onMouseUp 时返回 null，UIRuntimeScreen 据此跳过 screen 级派发。
     */
    @Suppress("TooGenericExceptionCaught")
    private fun buildScreenOnMouseUp(
        actions: MutableMap<String, List<StmtNode>>,
        evalCtx: EvalContext,
    ): UiEventHandler<MouseEvent>? {
        // reserved 键从 actionMap 移除，避免 EventBindingDispatcher 误派发为普通 action。
        val body = actions.remove(ReservedActions.SCREEN_ON_MOUSE_UP)
        if (body == null || body.isEmpty()) return null
        return UiEventHandler { event ->
            val eventMap = EventMapBuilder.mouseEventToMap(event)
            val local = evalCtx.withLocals(emptyMap<String, Any?>())
            local.declareVar(ReservedActions.SCREEN_ON_MOUSE_UP_PARAM, eventMap)
            try {
                ExprEvaluator.execute(body, local)
            } catch (t: Throwable) {
                LOGGER.warn("[ui] screen onMouseUp 执行异常 button=${event.buttonName()}", t)
            }
        }
    }

    /**
     * RFC-MOUSE-EXTENDED-EVENTS Stage B：与 [buildScreenOnMouseUp] 完全对称。
     *
     * 从 actionMap 取出 screen 级 onMouseMove lambda 体（保留键名见
     * [ReservedActions.SCREEN_ON_MOUSE_MOVE]），包装为 `UiEventHandler<MouseEvent>`：
     * 每次调用时把 MouseEvent 转为 Map 注入 EvalContext 栈顶帧，再执行 stmt 列表。
     *
     * 未声明 onMouseMove 时返回 null，UIRuntimeScreen 据此跳过 screen 级派发。
     */
    @Suppress("TooGenericExceptionCaught")
    private fun buildScreenOnMouseMove(
        actions: MutableMap<String, List<StmtNode>>,
        evalCtx: EvalContext,
    ): UiEventHandler<MouseEvent>? {
        // reserved 键从 actionMap 移除，避免 EventBindingDispatcher 误派发为普通 action。
        val body = actions.remove(ReservedActions.SCREEN_ON_MOUSE_MOVE)
        if (body == null || body.isEmpty()) return null
        return UiEventHandler { event ->
            val eventMap = EventMapBuilder.mouseEventToMap(event)
            val local = evalCtx.withLocals(emptyMap<String, Any?>())
            local.declareVar(ReservedActions.SCREEN_ON_MOUSE_MOVE_PARAM, eventMap)
            try {
                ExprEvaluator.execute(body, local)
            } catch (t: Throwable) {
                LOGGER.warn("[ui] screen onMouseMove 执行异常 x=${event.x()} y=${event.y()}", t)
            }
        }
    }

    /**
     * Phase 4：注册 HUD keybinding 到槽位池。
     *
     * 遍历 IR 中的 `keybinding` 声明，为每个绑定分配 KeyMapping 槽位。
     * HUD 销毁时由 [IrSession.dispose] 自动释放槽位。
     */
    private fun registerKeyBindings(ir: UiIr, session: IrSession) {
        val hudMeta: UiHudMeta = ir.hudMeta ?: return
        val keyBindings: List<UiKeyBinding>? = hudMeta.keyBindings
        if (keyBindings == null || keyBindings.isEmpty()) return
        for (kb in keyBindings) {
            // model 端 defaultKey / label 可空（.allin 可省略，见 hud.md §1.5）：defaultKey 省略即未绑定，
            // 一路透传到 DynamicKeyBindingManager.resolveKey(null) → UNKNOWN；label 省略即无显示名。
            // onTriggerBytes 由 serializeActionBody 保证非空，故仅此处保留 !!。
            session.registerKeyBinding(kb.id, kb.defaultKey, kb.label, kb.onTriggerBytes!!)
        }
    }

    private fun buildStates(ir: UiIr): Map<String, Signal<Any?>> {
        val map = LinkedHashMap<String, Signal<Any?>>()
        for (e in ir.stateDefaults.entries.entries) {
            // 路线 B：state 默认值若是 List/Map，递归包成反应式集合，
            // 让 state.x.add(y) / state.x.put(k,v) 等 mutate 操作直接触发订阅者重跑。
            map[e.key] = Reactive.signal<Any?>(ReactiveWrapper.wrap(e.value))
        }
        return map
    }

    /**
     * 把 IR 中的 derived 派生值表达式字节包成响应式 [top.wcpe.allincore.ui.runtime.reactive.Computed]。
     *
     * 每条 derived 的 supplier 在求值时对其表达式调用 [ExprEvaluator.eval]，读取
     * `state.x` / `derived.y` 时自动建立依赖订阅；上游变化时 Computed 失效，
     * 下次被读取（如某个 prop Effect 重跑）时重算。懒求值：未被任何 Effect 读取的 derived 不计算。
     *
     * 注意：Computed 的 supplier 捕获 `evalCtx` 引用，而 `evalCtx` 的 derived 表
     * 又指向本方法的返回值——因此调用方必须先创建好 evalCtx，再 attachDerived(buildDerived(ir, evalCtx))。
     * derived 之间的链式引用（derived.a 读 derived.b）通过共享同一张表正确解析。
     */
    private fun buildDerived(ir: UiIr, evalCtx: EvalContext): Map<String, Computed<Any?>> {
        val exprs: Map<String, ByteArray> = ir.derivedDefaults.exprs
        if (exprs.isEmpty()) {
            return emptyMap()
        }
        val map = LinkedHashMap<String, Computed<Any?>>(exprs.size)
        for (e in exprs.entries) {
            val expr: ExprNode = ExprBlobReader.readExpr(e.value)
            map[e.key] = Reactive.computed<Any?> { ExprEvaluator.eval(expr, evalCtx) }
        }
        return map
    }

    /**
     * 触发一次 session / persist 字段的"声明 + 默认值初始化"。
     *
     * SessionRef / PersistRef 在 ExprEvaluator 直接走 SharedStateRegistry 全局表读取，
     * 这里仅在 session 创建时把声明的字段集 + 默认值预先注册到注册表，避免首次访问
     * 时拿到 null（初始 default 为 null 会触发 reactive 初始追踪问题）。
     */
    private fun seedSharedStates(ir: UiIr, sharedStateAccess: SharedStateAccess) {
        for (e in ir.sessionStateDefaults.entries.entries) {
            sharedStateAccess.sessionSignal(e.key, e.value)
        }
        for (e in ir.persistStateDefaults.entries.entries) {
            sharedStateAccess.declarePersistField(e.key, e.value, ir.persistStateDefaults.types[e.key]) { message ->
                LOGGER.warn(message)
            }
        }
    }

    private fun resolveSharedStateAccess(requestDispatcher: RequestDispatcher): SharedStateAccess =
        (requestDispatcher as? SharedStateAccessProvider)?.sharedStateAccess ?: SharedStateRegistry.global()

    private fun parseActions(actions: Map<String, UiAction>): MutableMap<String, List<StmtNode>> {
        val map = LinkedHashMap<String, List<StmtNode>>()
        for (e in actions.entries) {
            // STATE_TRANSITION 的 bodyBytes 是 runtime 自定义编码，不可走 ExprBlobReader。
            if (e.value.kind == UiAction.Kind.STATE_TRANSITION) {
                continue
            }
            map[e.key] = ExprBlobReader.readActionBody(e.value.bodyBytes)
        }
        return map
    }
}
